import React, { ReactNode, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  useMultiSelectMenu,
  MultiSelectMenuProps,
  OptionType,
  Spinner,
} from '@fullstory/components';
import { ResourceDisplayPane } from '../../ResourceDisplayPane';
import css from '../index.module.scss';
import { Metadata, MetadataDisplay } from '../../MetadataDisplay';
import { ResourceSelectMenuItemType } from '..';
import { intl } from '@fullstory/intl';

interface ResourceMultiSelectMenuProps<T>
  extends MultiSelectMenuProps<T>,
    Pick<React.ComponentProps<typeof MetadataDisplay>, 'onModifyOwner'> {
  /**
   * String that identifies the type of item being filtered. (e.g. "Metric")
   */
  itemType: ResourceSelectMenuItemType;
  onCreate?: () => void;
  onEdit?: (option: OptionType<T>) => void;
  renderDefinition: (option: OptionType<T>) => ReactNode;
  getMetadata?: (option: OptionType<T>) => Metadata | null;
  canEdit: (option: OptionType<T>) => boolean | 'loading';
  optionsLoading?: boolean;
}

const resourceMultiSelectMenuMessages: {
  [key in ResourceSelectMenuItemType]: { createText: string };
} = {
  Element: {
    createText: intl.formatMessage({ defaultMessage: 'Create custom Element', id: '99TyfI' }),
  },
  Funnel: {
    createText: intl.formatMessage({ defaultMessage: 'Create custom Funnel', id: 'KS9p7O' }),
  },
  Metric: {
    createText: intl.formatMessage({ defaultMessage: 'Create custom Metric', id: 'esLGmk' }),
  },
  Option: {
    createText: intl.formatMessage({ defaultMessage: 'Create custom Option', id: 'N5pAPC' }),
  },
  Page: { createText: intl.formatMessage({ defaultMessage: 'Create custom Page', id: 'lBLKxX' }) },
  Segment: {
    createText: intl.formatMessage({ defaultMessage: 'Create custom Segment', id: 'sFcPai' }),
  },
  Space: {
    createText: intl.formatMessage({ defaultMessage: 'Create Space', id: 'v3aFf1' }),
  },
  hostname: {
    createText: intl.formatMessage({ defaultMessage: 'Create custom hostname', id: 'sAlOVS' }),
  },
  CustomEvent: {
    createText: '', // not used
  },
  attribute: {
    createText: intl.formatMessage({ defaultMessage: 'Create custom attribute', id: 'GRiz/A' }),
  },
  Connection: {
    createText: '', // not used
  },
  propertyRuleSource: {
    createText: '', // not used
  },
};

export function ResourceMultiSelectMenu<T>(props: ResourceMultiSelectMenuProps<T>) {
  const {
    options,
    selected,
    testId = 'resource-multi-select-menu',
    itemType,
    onCreate,
    onEdit,
    renderDefinition,
    getMetadata,
    canEdit: canEditFunc,
    onModifyOwner,
    selectedLimit = 5,
    optionsLoading,
    ...multiSelectMenuProps
  } = props;
  const [highlighted, setHighlighted] = useState<OptionType<T> | null>(null);

  const messages = resourceMultiSelectMenuMessages[itemType];

  const {
    Select,
    selectProps,
    Filter,
    filterProps,
    filterRef,
    Options,
    optionsProps,
    Footer,
    footerProps,
  } = useMultiSelectMenu({
    options,
    selected,
    selectedLimit,
    alwaysHovered: true,
    testId,
    onHighlight: setHighlighted,
    primaryAction: onCreate
      ? {
          action: onCreate,
          text: messages.createText,
        }
      : undefined,
    ...multiSelectMenuProps,
  });

  const [canEdit, setCanEdit] = useState<boolean | 'loading'>(false);

  useEffect(() => {
    if (highlighted) {
      setCanEdit(canEditFunc(highlighted));
    }
  }, [canEditFunc, highlighted]);

  return (
    <Select
      data-component="ResourceMultiSelectMenu"
      contentWidth="auto"
      onFirstUpdate={() => filterRef.current?.focus()}
      {...selectProps}
    >
      <Flex>
        <Flex column alignItems="stretch" justifyContent="stretch" className={css.menu}>
          {options.length > 10 && (
            <Box flexShrink={0}>
              <Filter ref={filterRef} {...filterProps} />
            </Box>
          )}
          <Options hoistSelected {...optionsProps} />
          {optionsLoading && (
            <Box height="50px">
              <Flex>
                <Spinner />
              </Flex>
            </Box>
          )}
          <Footer {...footerProps} />
        </Flex>
        {highlighted &&
          renderDefinition &&
          renderDefinition(highlighted) &&
          // The highlighted item doesn't get reset when no options match the filter.
          // So, if there are no options we should not show the pane.
          optionsProps.options.length > 0 && (
            <ResourceDisplayPane>
              <ResourceDisplayPane.Header
                label={highlighted.label}
                icon={highlighted.icon}
                onOpen={() => (onEdit ? onEdit(highlighted) : undefined)}
                canAccess={canEdit}
              />
              <ResourceDisplayPane.Body>{renderDefinition(highlighted)}</ResourceDisplayPane.Body>
              {getMetadata && getMetadata(highlighted) && (
                <ResourceDisplayPane.Footer
                  metadata={getMetadata(highlighted)!}
                  onModifyOwner={onModifyOwner}
                />
              )}
            </ResourceDisplayPane>
          )}
      </Flex>
    </Select>
  );
}
